// Only run the orphan-SW sweep once per page load.
let orphanCleanupDone = false;

/**
 * Unregisters any service worker whose script is not `/service-worker.js`
 * (matched by path so query params are ignored). Our own registration is never touched.
 */
async function cleanupOrphanServiceWorkers(){
    if(orphanCleanupDone || !("serviceWorker" in navigator)) return;
    orphanCleanupDone = true;
    try{
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(async (reg) => {
            const scriptUrl = reg.active?.scriptURL ?? reg.waiting?.scriptURL ?? reg.installing?.scriptURL ?? "";
            if(scriptUrl && !scriptUrl.split("?")[0].endsWith("/service-worker.js")){
                await reg.unregister();
            }
        }));
    }catch{
        // Best-effort; a failed sweep must never block app startup.
    }
}

/**
 * Marks this tab as the last-used one so a newly opened tab inherits its auth
 * config (a small step toward per-tab multi-account support).
 */
function markActiveTab(){
    if("serviceWorker" in navigator && navigator.serviceWorker.controller){
        navigator.serviceWorker.controller.postMessage({action:"activate"});
    }
}

// Reload at most once per page load; guards the auto-update against loops.
let reloadingForUpdate = false;

/**
 * Reloads the page the instant a freshly-deployed worker takes control so a new
 * version is always applied immediately. Only wired when a controller already
 * exists at load time (first install has no prior controller). Guarded to fire once.
 */
function autoReloadOnUpdate(){
    if(!("serviceWorker" in navigator) || !navigator.serviceWorker.controller) return;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
        if(reloadingForUpdate) return;
        reloadingForUpdate = true;
        window.location.reload();
    });
}

/** Performs the actual (one-time) registration; see {@link RegisterServiceWorker}. */
function createRegistration(): Promise<ServiceWorkerRegistration> {
    return new Promise<ServiceWorkerRegistration>((resolve, reject) => {

        const timeout = setTimeout(() => {
            reject("Registration timed out");
        }, 5000);
        const buildnr = import.meta.env.VITE_BUILD_HASH? import.meta.env.VITE_BUILD_HASH.substring(0,8) : 'DEV';
        const environmentConfig = new URLSearchParams({
            apiUrl: import.meta.env.VITE_API_URL,
            build: buildnr,
            cacheFE: import.meta.env.VITE_CACHE_FRONTEND??"0",
        }).toString();

        // Wire the auto-reload BEFORE registering so no controllerchange slips past.
        autoReloadOnUpdate();

        void cleanupOrphanServiceWorkers();

        navigator.serviceWorker.register(`/service-worker.js?${environmentConfig}`).then(function (registration) {
            // Check now and on focus so long-lived tabs pick up new builds without manual navigation.
            const checkForUpdate = () => { void registration.update().catch(() => {}); };
            checkForUpdate();
            window.addEventListener("focus", () => {
                markActiveTab();
                checkForUpdate();
            });
            navigator.serviceWorker.ready.then((readyReg) => {
                clearTimeout(timeout);
                markActiveTab();
                resolve(readyReg);
            });
        }).catch(function (err) {
            clearTimeout(timeout);
            reject(err);
        });
    });
}

let registrationPromise: Promise<ServiceWorkerRegistration> | null = null;

/**
 * Registers our service worker and resolves with the ready registration.
 * Memoized: every caller in a session shares one registration attempt so the
 * update/focus listeners are wired exactly once. A rejected attempt clears the
 * memo so a later call can retry.
 */
export function RegisterServiceWorker(): Promise<ServiceWorkerRegistration> {
    if (!registrationPromise) {
        registrationPromise = createRegistration();
        registrationPromise.catch(() => { registrationPromise = null; });
    }
    return registrationPromise;
}
